import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import moment from 'moment';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import PlanningSupportForm from '~/components/PlanningSupportForm';
import { dataTransferFormat } from '~/utils/date';
import FinalizeWeekMenuItem from '../FinalizeWeekMenuItem';
import ImportShiftsMenuItem from '../ImportShiftsMenuItem';
import ReleaseWeekMenuItem from '../ReleaseWeekMenuItem';
import messages from './messages';

type WeekActionsMenuProps = {
  week: number;
  year: number;
  cutoffDates: {
    shiftsReleasedUntil?: string;
    shiftsFinalizedUntil?: string;
  };
  weekHasShifts: boolean;
  weekHasCommittedShifts: boolean;
  weekHasPrivateShifts: boolean;
};

const WeekActionsMenu = (props: WeekActionsMenuProps) => {
  const intl = useIntl();
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLButtonElement>();

  const firstDayOfWeek = moment()
    .isoWeekYear(props.year)
    .isoWeek(props.week)
    .startOf('week');
  const lastDayOfWeek = dataTransferFormat(
    moment(firstDayOfWeek).endOf('week')
  );
  const lastWeek = moment(firstDayOfWeek).subtract(1, 'weeks');
  const weekIsReleased = firstDayOfWeek.isBefore(
    props.cutoffDates.shiftsReleasedUntil
  );
  const weekIsFinalized = firstDayOfWeek.isBefore(
    props.cutoffDates.shiftsFinalizedUntil
  );

  const shouldShowDownloadDraftItem = weekIsReleased && props.weekHasShifts;
  const shouldShowFinalizeWeekItem =
    weekIsReleased &&
    !weekIsFinalized &&
    props.weekHasShifts &&
    props.weekHasCommittedShifts;
  const shouldShowReleaseWeekItem =
    !weekIsReleased && props.weekHasShifts && props.weekHasPrivateShifts;
  const shouldShowImportShiftsItem = !props.weekHasShifts;

  const handleClose = () => {
    setActionMenuAnchor(undefined);
  };

  return shouldShowDownloadDraftItem ||
    shouldShowFinalizeWeekItem ||
    shouldShowReleaseWeekItem ||
    shouldShowImportShiftsItem ? (
    <>
      <IconButton
        aria-label={intl.formatMessage(messages.showActionMenu)}
        aria-controls="action-menu"
        aria-haspopup="true"
        onClick={(event) => {
          setActionMenuAnchor(event.currentTarget);
        }}
        color="inherit"
        size="large"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        anchorEl={actionMenuAnchor}
        keepMounted
        open={Boolean(actionMenuAnchor)}
        onClose={handleClose}
      >
        {shouldShowDownloadDraftItem && (
          <PlanningSupportForm
            weekDate={firstDayOfWeek.toISOString()}
            onCloseMenu={handleClose}
          />
        )}
        {shouldShowFinalizeWeekItem && (
          <FinalizeWeekMenuItem
            endOfWeekDate={lastDayOfWeek}
            onCloseMenu={handleClose}
          />
        )}
        {shouldShowReleaseWeekItem && (
          <ReleaseWeekMenuItem
            releaseUntil={lastDayOfWeek}
            onCloseMenu={handleClose}
          />
        )}
        {shouldShowImportShiftsItem && (
          <ImportShiftsMenuItem
            templateRange={{
              year: lastWeek.isoWeekYear(),
              week: lastWeek.isoWeek(),
            }}
            targetRange={{
              year: props.year,
              week: props.week,
            }}
            onCloseMenu={handleClose}
          />
        )}
      </Menu>
    </>
  ) : null;
};

export default WeekActionsMenu;
